import { useRef } from "react";
import { FileUp, FileText, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ACCEPT, fmtBytes } from "@/components/submittals/submittalMeta";

/** Multi-file picker with a removable list — PDFs, images, CAD, Office, ZIP. */
export const FilePicker = ({ files, setFiles, max = 10, testPrefix = "filepicker", label = "Add files" }) => {
  const ref = useRef(null);
  const add = (list) => {
    const next = [...files];
    for (const f of Array.from(list || [])) {
      if (next.length >= max) break;
      if (!next.some((x) => x.name === f.name && x.size === f.size)) next.push(f);
    }
    setFiles(next);
  };
  return (
    <div className="space-y-2" data-testid={`${testPrefix}-wrap`}>
      <input ref={ref} type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => { add(e.target.files); e.target.value = ""; }} data-testid={`${testPrefix}-input`} />
      <button
        type="button"
        onClick={() => ref.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); add(e.dataTransfer.files); }}
        className="flex h-20 w-full flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-border bg-background text-sm text-muted-foreground transition-colors duration-150 hover:border-primary/50 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        data-testid={`${testPrefix}-dropzone`}
      >
        <FileUp className="h-5 w-5 text-primary" />
        <span>{label} — PDF, images, CAD, Office, ZIP (up to {max})</span>
      </button>
      {files.length > 0 && (
        <ul className="divide-y divide-border rounded-lg border border-border" data-testid={`${testPrefix}-list`}>
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`} className="flex items-center gap-2 px-3 py-2 text-sm" data-testid={`${testPrefix}-file-${i}`}>
              <FileText className="h-4 w-4 shrink-0 text-primary" />
              <span className="min-w-0 flex-1 truncate">{f.name}</span>
              <span className="text-xs text-muted-foreground">{fmtBytes(f.size)}</span>
              <Button type="button" size="icon" variant="ghost" className="h-7 w-7" onClick={() => setFiles(files.filter((_, j) => j !== i))} aria-label={`Remove ${f.name}`} data-testid={`${testPrefix}-remove-${i}`}>
                <X className="h-3.5 w-3.5" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
